import { useRef } from 'react';
import classNames from 'classnames';

import Button from 'components/partials/button/button';
import SegmentedTab from 'components/partials/segmented-tab/segmented-tab';
import { useFeatureHintTarget } from 'components/shared/feature-hints';

import { useElementPngExport } from 'hooks/use-element-png-export';

import Icons from 'assets/icons';

import MarkContextTrigger from 'containers/admin/clients/client/tabs-components/ai-assistant/mark-panel/components/mark-context-trigger';

import type { FunnelDisplayMode } from 'containers/user/critical-elements-monitoring/components/funnel-stage-card';
import FunnelCard from 'containers/user/critical-elements-monitoring/components/funnel-card';
import FunnelCardGrid from 'containers/user/critical-elements-monitoring/components/funnel-card-grid';
import FunnelCardRowSkeleton from 'containers/user/critical-elements-monitoring/components/funnel-card-row-skeleton';
import FunnelExportLayout from 'containers/user/critical-elements-monitoring/components/funnel-export-layout';
import { buildFunnelCardRow } from 'containers/user/critical-elements-monitoring/helpers/build-funnel-card-row';
import type { ProspectAvailability } from 'containers/user/critical-elements-monitoring/helpers/get-prospect-availability';
import type {
  CompareTo,
  ExtendWithOption,
  FunnelSection,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';
import {
  FUNNEL_PANEL_CLASSES,
  getExtensionDisplayLabel,
  getStageDisplayLabel,
} from 'containers/user/critical-elements-monitoring/monitoring-constants';
import {
  isMarkTriggerEligible,
  MONITORING_MARK_SECTION,
} from 'containers/user/critical-elements-monitoring/mark/monitoring-mark-context';
import { MONITORING_HINT_ID } from 'containers/user/critical-elements-monitoring/walkthrough/monitoring-hints.config';

export interface TotalFunnelSectionProps {
  funnel: FunnelSection | undefined;
  enteringClass: number;
  compareTo: CompareTo;
  displayMode: FunnelDisplayMode;
  onDisplayModeChange: (displayMode: FunnelDisplayMode) => void;
  selectedUnentitledExtensions: readonly ExtendWithOption[];
  skeletonCardCount: number;
  prospectAvailability: ProspectAvailability;
  isContentLoaded: boolean;
}

const TotalFunnelSection = ({
  funnel,
  enteringClass,
  compareTo,
  displayMode,
  onDisplayModeChange,
  selectedUnentitledExtensions,
  skeletonCardCount,
  prospectAvailability,
  isContentLoaded,
}: TotalFunnelSectionProps) => {
  const funnelDisplayModeHint = useFeatureHintTarget(
    MONITORING_HINT_ID.funnelDisplayMode
  );
  const exportLayoutRef = useRef<HTMLDivElement>(null);
  const { exportPng, isExporting } = useElementPngExport({
    targetRef: exportLayoutRef,
    fileName: `Total funnel ${enteringClass}`,
  });
  const cardRow = funnel
    ? buildFunnelCardRow(funnel, selectedUnentitledExtensions)
    : [];

  return (
    <section className="flex flex-col gap-3" data-testid="total-funnel-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="group flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-700">
              Total funnel
            </h2>
            <MarkContextTrigger
              target={{
                kind: 'section',
                sectionId: MONITORING_MARK_SECTION.totalFunnel,
              }}
              ariaLabel="Ask MARK about Total funnel"
            />
          </div>
          <p className="text-xs text-gray-500">
            Entering class {enteringClass}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isContentLoaded && (
            <SegmentedTab
              {...funnelDisplayModeHint}
              label="Funnel display mode"
              value={displayMode}
              onChange={onDisplayModeChange}
              options={[
                { value: 'volume', label: 'Volume' },
                {
                  value: 'rate',
                  label: 'Rate',
                  disabled: compareTo === 'prior-year',
                },
              ]}
            />
          )}

          <Button
            variant="reserve-outlined"
            size="baby"
            iconBefore={<Icons.Download aria-hidden="true" />}
            onClick={() => exportPng()}
            disabled={!funnel}
            loading={isExporting}
            data-testid="total-funnel-export"
          >
            Export
          </Button>
        </div>
      </div>

      <div className={classNames(FUNNEL_PANEL_CLASSES, 'w-0 min-w-full')}>
        {funnel ? (
          <FunnelCardGrid>
            {cardRow.map((item) => (
              <FunnelCard
                key={item.key}
                item={item}
                compareTo={compareTo}
                displayMode={displayMode}
                cohortLabel={funnel.cohortLabel}
                prospectAvailability={prospectAvailability}
                renderStageHeaderAction={(stage) =>
                  isMarkTriggerEligible(stage, displayMode) ? (
                    <MarkContextTrigger
                      target={{
                        kind: 'tile',
                        sectionId: MONITORING_MARK_SECTION.totalFunnel,
                        tileId: stage.stageKey,
                        displayMode,
                      }}
                      ariaLabel={`Ask MARK about ${getStageDisplayLabel(
                        stage.stageKey,
                        displayMode
                      )}`}
                    />
                  ) : null
                }
                renderExtensionHeaderAction={(extension) =>
                  isMarkTriggerEligible(extension, displayMode) ? (
                    <MarkContextTrigger
                      target={{
                        kind: 'tile',
                        sectionId: MONITORING_MARK_SECTION.totalFunnel,
                        tileId: extension.type,
                        displayMode,
                      }}
                      ariaLabel={`Ask MARK about ${getExtensionDisplayLabel(
                        extension.type,
                        displayMode
                      )}`}
                    />
                  ) : null
                }
              />
            ))}
          </FunnelCardGrid>
        ) : (
          <FunnelCardRowSkeleton cardCount={skeletonCardCount} />
        )}
      </div>

      {isExporting && funnel && (
        <FunnelExportLayout
          ref={exportLayoutRef}
          cardRow={cardRow}
          enteringClass={enteringClass}
          compareTo={compareTo}
          displayMode={displayMode}
          cohortLabel={funnel.cohortLabel}
          prospectAvailability={prospectAvailability}
        />
      )}
    </section>
  );
};

export default TotalFunnelSection;
